import {
  Dashlet,
  DashletApiDateFilters,
  DashletData,
  DashletQuestionListTypeFilter,
  DashletType,
  DateFilterSearch,
  QuestionCompareListDashletConfig,
  QuestionDataFilters,
  QuestionFilters,
  QuestionListAdditionalData,
  QuestionListDashletBaseConfig,
  QuestionListFormValues,
  QuestionListTableFilterValues,
  QuestionTypeFilter,
  dashletTypes,
} from "types";
import {
  QuestionDetailUrlArgs,
  defaultDashletQuestionSearchFilterValues,
  defaultQuestionSearchTableFilterValues,
  encodeTimeviewBucketDisplay,
  getQuestionSearchDateFilterParams,
  matchQuestionFilter,
} from "utils/questions";
import { formatDate, isNullOrUndefOrEmpty } from "@civicscience/chops";
import { mapBy, partition } from "utils/base";
import { mapDateFilterSearchToApiDates, parseDateLike } from "utils/dates";

import { QuestionOwnershipTerms } from "consts";
import { SortTypes } from "utils/search/sorting";
import { apiDateFilterToDateFilterSearch } from "utils/dates/formatDateFilter";
import csTagNodeUtils from "utils/taxonomy/csTagNodeUtils";
import { range } from "@civicscience/chops";

/**
 * Defines the minimum data needed to sort a Dashlet.
 * This is helpful for unit tests so you don't need to provide a fully populated Dashlet.
 */
export type DashletOrdering = Pick<Dashlet, "type" | "row" | "col">;

/**
 * Defines the shape of dashlet config which can be mapped to question filters.
 */
type QuestionBasedDashletConfig = QuestionDataFilters &
  DashletApiDateFilters & {
    qid: number | string;
    valueGroupingUri?: string | null;
    scoreUri?: string | null;
  };

type OrderDashletsResult<T extends DashletOrdering> = {
  dashletsAsColumns: T[][];
  dashletsOrdered: T[];
};

/**
 * Compares 2 dashlets based on their `row` value.
 */
const compareDashlets = <T extends DashletOrdering>(a: T, b: T) => a.row - b.row;

const combineDashletColumns = <T>(dashlets: T[][]) => {
  const [left, right] = dashlets;
  return range(Math.max(left.length, right.length))
    .flatMap((x) => [left[x], right[x]])
    .filter(Boolean);
};

/**
 * Partitions Dashlet's into 2 ordered columns as well as a flattened ordering.
 * The flattened ordering works across columns and represents the order in which
 * the dashlets should load on the dashboard.
 */
export const partitionAndOrderDashlets = <T extends DashletOrdering>(dashlets: T[] = []): OrderDashletsResult<T> => {
  const [unsortedLeft, unsortedRight] = partition(dashlets, (x) => x.col === 0);
  const dashletsAsColumns = [unsortedLeft.sort(compareDashlets), unsortedRight.sort(compareDashlets)];

  return { dashletsAsColumns, dashletsOrdered: combineDashletColumns(dashletsAsColumns) };
};

/**
 * Returns a new list of Dashlets with the provided `editedDashlet` replacing
 * the existing by id value.
 */
export const replaceDashlet = (editedDashlet: Dashlet, dashlets: Dashlet[]): Dashlet[] =>
  mapBy(
    (x) => x.dashletUuid === editedDashlet.dashletUuid,
    () => editedDashlet,
    dashlets,
  );

/**
 * Uses the provided Dashlet as the basis to create a "Clone" Dashlet.
 */
export const createCloneItem = <T extends Dashlet>(baseDashlet: T): T => {
  return {
    ...baseDashlet,
    dashletUuid: "",
    config: {
      ...baseDashlet.config,
      dashletName: baseDashlet.config.dashletName ? `[CLONE] - ${baseDashlet.config.dashletName}` : "",
    },
  };
};

export const sortFlatDashletsByColAndRow = (dashlets: Dashlet[]) => {
  return dashlets.sort((a, b) => {
    if (a.col < b.col) {
      return -1;
    } else if (a.col > b.col) {
      return 1;
    } else {
      if (a.row < b.row) {
        return -1;
      } else if (a.row > b.row) {
        return 1;
      } else {
        return 0;
      }
    }
  });
};

export const reorderDashlets = (dashlets: Dashlet[][], sourceDashlet?: Dashlet, targetDashlet?: Dashlet): Dashlet[] => {
  if (!sourceDashlet || !targetDashlet || sourceDashlet === targetDashlet) return dashlets[0].concat(dashlets[1]);
  const sortInColumn = (dashletsCol: Dashlet[], sourceDashlet: Dashlet, targetDashlet: Dashlet): Dashlet[] => {
    const sourceIndex = dashletsCol.findIndex((dash) => dash.dashletUuid === sourceDashlet.dashletUuid);
    const targetIndex = dashletsCol.findIndex((dash) => dash.dashletUuid === targetDashlet.dashletUuid);
    const newDashlets = [...dashletsCol];

    if (sourceIndex < targetIndex) {
      let currentIndex = sourceIndex;
      while (currentIndex <= targetIndex && newDashlets[currentIndex + 1]) {
        newDashlets[currentIndex] = newDashlets[currentIndex + 1];
        currentIndex++;
      }
    } else {
      let currentIndex = sourceIndex;
      while (currentIndex >= targetIndex && newDashlets[currentIndex - 1]) {
        newDashlets[currentIndex] = newDashlets[currentIndex - 1];
        currentIndex--;
      }
    }
    newDashlets[targetIndex] = sourceDashlet;
    return newDashlets;
  };

  const removeFromColumn = (dashlets: Dashlet[], sourceDashlet: Dashlet): Dashlet[] => {
    const sourceIndex = dashlets.findIndex((dash) => dash.dashletUuid === sourceDashlet.dashletUuid);
    const newDashlets = [...dashlets];
    newDashlets.splice(sourceIndex, 1);

    return newDashlets;
  };

  const insertIntoColumn = (dashlets: Dashlet[], sourceDashlet: Dashlet, targetDashlet: Dashlet): Dashlet[] => {
    const targetIndex = dashlets.findIndex((dash) => dash.dashletUuid === targetDashlet.dashletUuid);
    const newDashlets = [...dashlets];
    newDashlets.splice(targetIndex, 0, sourceDashlet);
    sourceDashlet.col = targetDashlet.col;

    return newDashlets;
  };

  const newDashlets = structuredClone(dashlets);
  newDashlets[0] = sortFlatDashletsByColAndRow(newDashlets[0]);
  newDashlets[1] = sortFlatDashletsByColAndRow(newDashlets[1]);
  if (sourceDashlet.col === targetDashlet.col) {
    newDashlets[sourceDashlet.col] = sortInColumn(newDashlets[sourceDashlet.col], sourceDashlet, targetDashlet);
  } else {
    newDashlets[sourceDashlet.col] = removeFromColumn(newDashlets[sourceDashlet.col], sourceDashlet);
    newDashlets[targetDashlet.col] = insertIntoColumn(newDashlets[targetDashlet.col], sourceDashlet, targetDashlet);
  }

  const dashletsWithRowsUpdated = newDashlets.map((col) =>
    col.map((dash, dashIndex) => {
      return { ...dash, row: dashIndex };
    }),
  );

  return dashletsWithRowsUpdated[0].concat(dashletsWithRowsUpdated[1]);
};

export const appendDashletToCol = (
  columnNumber: string | undefined,
  sourceDashlet: Dashlet,
  dashletsColumns: Dashlet[][],
) => {
  if (!columnNumber || !sourceDashlet || !dashletsColumns) return dashletsColumns[0].concat(dashletsColumns[1]);
  const deleteFromColumn = (column: Dashlet[], sourceDashlet: Dashlet) => {
    const sourceIndex = column.findIndex((dash) => dash.dashletUuid === sourceDashlet.dashletUuid);
    column.splice(sourceIndex, 1);
  };

  const updateColsRows = (columns: Dashlet[][]) => {
    return columns.map((col, iCol) => col.map((dash, iRow) => ({ ...dash, col: iCol, row: iRow })));
  };
  if (columnNumber) {
    deleteFromColumn(dashletsColumns[sourceDashlet.col], sourceDashlet);
    dashletsColumns[Number(columnNumber)].push(sourceDashlet);
    const dashletsColumnsRenumbered = updateColsRows(dashletsColumns);
    const newDashletsFlat = dashletsColumnsRenumbered[0].concat(dashletsColumnsRenumbered[1]);
    return newDashletsFlat;
  }
};

/**
 * Returns `true` if the provided `dashType` is supported; otherwise `false`.
 */
export const isDashletSupported = (dashType: string): boolean => dashletTypes.includes(dashType as DashletType);

/**
 * Returns `true` if there is at least 1 unsupported dashlet in the list; otherwise `false`.
 */
export const hasUnsupportedDashlet = (dashlets: Dashlet[]): boolean =>
  dashlets.some((x) => !isDashletSupported(x.type));

/**
 * A dashlet's exported result data (`DashletData`) plus its `type` and `config` -
 * e.g. giving a dashlet its name, or for Comment dashlets, the actual comment text.
 * `type`/`config` are omitted if no matching dashlet was found for the entry's `dashletId`.
 */
export type EnrichedDashletExportData = DashletData & {
  type?: Dashlet["type"];
  config?: Dashlet["config"];
};

/**
 * Enriches each dashlet's raw exported data (as collected for the Dashboard export mutation)
 * with its `type` and `config`, joining against the dashboard's own dashlet list by uuid.
 */
export const enrichExportDataWithDashlets = (
  exportData: DashletData[],
  dashlets: Dashlet[] = [],
): EnrichedDashletExportData[] => {
  const dashletsByUuid = new Map(dashlets.map((dashlet) => [dashlet.dashletUuid, dashlet]));
  return exportData.map(({ dashletId, data }) => {
    const dashlet = dashletsByUuid.get(dashletId);
    return { dashletId, type: dashlet?.type, config: dashlet?.config, data };
  });
};

/**
 * Maps the dashlet's "sort" value to the API params for ordering.
 * You can spread the result into the final params you pass to the API.
 */
export const getOrderOptionsForDashletSort = (
  sortValue: string | undefined,
): { order: string; orderBy: string } | null => {
  switch (sortValue) {
    case "oldest":
      return { order: "asc", orderBy: "question.created_time" };
    case "responses":
      return { order: "desc", orderBy: "response_count" };
    case "relevance":
      return null;
    default:
      return { order: "desc", orderBy: "question.created_time" };
  }
};

/**
 * Maps the dashlet's question type value to the API value for question type.
 */
export const getQuestionTypeFromDashletQuestionType = (questionType: string | null): QuestionTypeFilter[] => {
  switch (questionType) {
    case "custom":
      return ["type_custom"];
    case "syndicated":
      return ["type_syndicated"];
    default:
      return ["type_custom", "type_syndicated"];
  }
};

/**
 * Maps the `sorts` propery from dashlet config to a sort value used by Question search.
 * Note: The return value is what is stored in the *query string* NOT what is sent to the API call.
 * See `getOrderOptionsForDashletSort` for translating to the API value.
 */
export const getSortTermFromDashletSorts = (sorts: string[]): string | undefined => {
  const sort = sorts[0];
  if (!sort) {
    return undefined;
  }

  switch (sort) {
    case "newest":
      return SortTypes.NEWEST_CREATED;
    case "oldest":
      return SortTypes.OLDEST_CREATED;
    case "responses":
      return SortTypes.RESPONSES;
    case "relevance":
      return SortTypes.RELEVANCE;
  }
};

/**
 * Maps the Question List sort term to Dashlet List config sort term.
 * This is the inverse mapping of `getSortTermFromDashletSorts` above
 * and used when we want to create a dashlet from a user's current question search.
 */
export const getDashletSortsFromQuestionSortTerm = (sort: string): string[] => {
  if (!sort) {
    return [];
  }

  switch (sort) {
    case SortTypes.NEWEST_CREATED:
      return ["newest"];
    case SortTypes.OLDEST_CREATED:
      return ["oldest"];
    case SortTypes.RESPONSES:
      return ["responses"];
    case SortTypes.RELEVANCE:
      return ["relevance"];
    default:
      return [];
  }
};

type DashletSearchConfig = QuestionListDashletBaseConfig & {
  sorts?: string[];
  weightingMode?: string | null;
  additionalData?: QuestionListAdditionalData;
};
/**
 * Maps a `DashletSearchConfig` to question search filters.
 * This is useful for QuestionList and QuestionCompareList so that they can easily use question search utils
 * designed to work with the question search page.
 */
export const mapDashletSearchConfigToQuestionSearch = (
  config: DashletSearchConfig,
): QuestionListFormValues & QuestionListTableFilterValues => {
  const { filters } = config;

  return {
    ...defaultDashletQuestionSearchFilterValues,
    ...defaultQuestionSearchTableFilterValues,
    dateFilter: {
      kind: "custom-fixed",
      startDate: parseDateLike(filters?.launchedAfter),
      endDate: parseDateLike(filters?.completedBefore),
    },
    isMyFavoritesOnly: filters?.isWatchlist ?? defaultDashletQuestionSearchFilterValues.isMyFavoritesOnly,
    query: filters?.search || defaultDashletQuestionSearchFilterValues.query,
    questionType: filters?.questionType
      ? getQuestionTypeFromDashletQuestionType(filters.questionType)
      : defaultDashletQuestionSearchFilterValues.questionType,
    questionRole: filters?.questionRole
      ? [filters.questionRole]
      : defaultDashletQuestionSearchFilterValues.questionRole,
    sortTerm: getSortTermFromDashletSorts(config.sorts ?? []) || "",
    taxonomyTags:
      config.additionalData?.tagsFull?.map((tag) => csTagNodeUtils.encodeRootTagValue(tag, tag.accountRefid)) ??
      defaultDashletQuestionSearchFilterValues.tags,
  };
};

export const mapQuestionOwnershipTermToApiValue = (term: string): DashletQuestionListTypeFilter | null => {
  switch (term) {
    case QuestionOwnershipTerms.SYNDICATED:
      return "syndicated";
    case QuestionOwnershipTerms.CUSTOM:
      return "custom";
    default:
      return null;
  }
};

type QuestionFilterParams = {
  qWscheme: string | null;
  qDateFilter: DateFilterSearch;
};

export const mapDashletConfigToQuestionFilterParams = (
  config: Omit<QuestionCompareListDashletConfig, "qid">,
): QuestionFilterParams => {
  const hasDateFilter =
    !isNullOrUndefOrEmpty(config.filters?.completedBefore) || !isNullOrUndefOrEmpty(config.filters?.launchedAfter);
  const dateFilter: DateFilterSearch = hasDateFilter
    ? {
        kind: "custom-fixed",
        startDate: parseDateLike(config.filters?.launchedAfter),
        endDate: parseDateLike(config.filters?.completedBefore),
      }
    : { kind: "all" };
  const filterParams = {
    qWscheme: config.weightingMode ?? null,
    qDateFilter: dateFilter,
  };
  return filterParams;
};

/**
 * A list of count options to be displayed in a select element for question list dashlets.
 */
export const countOptions: Readonly<{ text: string; value: string }[]> = [
  { text: "Show 5 Questions", value: "5" },
  { text: "Show 10 Questions", value: "10" },
  { text: "Show 15 Questions", value: "15" },
  { text: "Show 20 Questions", value: "20" },
];

/**
 * A list of sort options to be displayed in a select element for question list dashlets.
 */
export const questionListSortOptions: Readonly<{ text: string; value: string }[]> = [
  { text: "Show newest questions first", value: "newest" },
  { text: "Show oldest questions first", value: "oldest" },
  { text: "Show questions with the most responses first", value: "responses" },
  { text: "Show the most relevant questions first", value: "relevance" },
];

/**
 * Translates the question list form values into question list dashlet config.
 * Used when creating both QuestionList and QuestionCompareList dashlets.
 */
export const questionListFormValuesToQuestionListDashletConfig = (
  values: QuestionListFormValues,
): QuestionListDashletBaseConfig => {
  const { dateFilter, taxonomyTags, questionRole, questionType } = values;

  const [csTags] = partition(taxonomyTags, csTagNodeUtils.isTagNodeValue);
  const apiTags = csTagNodeUtils.buildDashletApiValues(csTags);

  const apiQuestionType = questionType.length === 1 ? mapQuestionOwnershipTermToApiValue(questionType[0]) : null;
  const apiQuestionRole = questionRole.length === 1 ? questionRole[0] : null;

  const apiDates = getQuestionSearchDateFilterParams(dateFilter);
  const dateFormatOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
  const endDateString = apiDates.endDate ? apiDates.endDate.toDateString() : null;
  const startDateString = apiDates.startDate ? apiDates.startDate.toDateString() : null;

  return {
    count: null,
    filters: {
      completedBefore: endDateString ? formatDate(endDateString, dateFormatOptions) : null,
      launchedAfter: startDateString ? formatDate(startDateString, dateFormatOptions) : null,
      questionRole: apiQuestionRole,
      questionType: apiQuestionType,
      search: values.query ?? "",
      isWatchlist: values.isMyFavoritesOnly,
      tags: apiTags,
      feed: null,
      target: null,
      isAccount: false,
    },
  };
};

/**
 * Maps dashlet config to question url args.
 * Useful for question based dashlets like Question Results, TimeviewResults, etc.
 * to easily generate a link to the question detail page preserving query params.
 *
 * See also: `questionRouteUtils`
 */
export const mapDashletConfigToQuestionUrlArgs = (config: QuestionBasedDashletConfig): QuestionDetailUrlArgs => ({
  questionId: config.qid,
  displayOption: config.valueGroupingUri
    ? { kind: "grouping", groupingUri: config.valueGroupingUri }
    : config.scoreUri
      ? { kind: "score", scoreUri: config.scoreUri }
      : undefined,
  segmentUUID: config.segmentUuid,
  targetId: config.targetId,
  network: config.networkUuid,
  weightingSchemeName: config.weightingMode,
  dateFilter: apiDateFilterToDateFilterSearch(config),
  tvDateFilter:
    config.viewType && config.bucketMode ? encodeTimeviewBucketDisplay(config.viewType, config.bucketMode) : undefined,
});

/**
 * Helper to get correct api date values for dashboard bulk filters save
 *
 * The "all" global date filter is equivalent to a not applied date filter
 * Return "all" as an empty dictionary to ensure passed date configs are all undefined
 * Otherwise, map the date filter to the API format
 */
const overrideDateFilterForBulkSave = (globalDateFilter: DateFilterSearch) =>
  globalDateFilter.kind === "all" ? {} : mapDateFilterSearchToApiDates(globalDateFilter);

/**
 * Maps global filters to bulk dashlet save API values.
 */
export const mapDashboardGlobalFiltersToBulkDashletSaveArgs = (filters: QuestionFilters) => ({
  // not-applied filters are mapped to undefined to allow for easy exclusion in backend model dump
  // clear-current-value filters are mapped to null to allow for easy setting to None of the current value
  segmentUuid: matchQuestionFilter(filters.segment, {
    applied: (x) => x,
    "not-applied": () => undefined,
    "clear-current-value": () => null,
  }),
  targetId: matchQuestionFilter(filters.target, {
    applied: (x) => x,
    "not-applied": () => undefined,
    "clear-current-value": () => null,
  }),
  weightingMode: matchQuestionFilter(filters.weightingScheme, {
    applied: (x) => x,
    "not-applied": () => undefined,
    "clear-current-value": () => null,
  }),
  networkUuid: matchQuestionFilter(filters.network, {
    applied: (x) => x,
    "not-applied": () => undefined,
    "clear-current-value": () => null,
  }),
  ...overrideDateFilterForBulkSave(filters.dateFilter),
});
